<script setup lang="ts">
import {
  ContractManagementContractFileType,
  VaultFileRevisionKind,
  type ContractManagementContractFileInDb,
  type ContractManagementContractFileInDbExtended,
  type ContractManagementExtractionInDb,
  type ContractManagementContractFileUpdateUser,
  type ContractManagementViewColumnInDb,
  type ContractManagementContractInDb,
  type VaultFileRevisionInDb,
  type DocumentState,
  type ContractManagementObligationInDb,
  type ContractManagementClauseInDb,
  type ContractManagementDateInDb,
  type ContractManagementExtractionSearchResult,
  TaskStatus,
  getContractManagementContractFileContractualParties,
} from '@/api';
import type { ContractualParty } from '@/services/contractManagement/contractualPartyRow';
import { enumToSelectOptionsWithLabels } from '@/utils/enumUtils';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import { ContractFileDownloadService } from '@/services/contractManagement/contractFileDownloadService';
import { ContractFileDetailService } from '@/services/contractManagement/contractFileDetailService';
import { IconClose, IconMore } from '@/components/design/icons';
import { contractFileTypeLabel } from '@/components/design/statusMaps';
import GaiaContractStateCell from '@/components/design/domain/contractManagement/GaiaContractStateCell.vue';
import { fileTypeHasState } from '@/components/design/domain/contractManagement/contractStateIcons';
import { isConversionRunning, isPdfFile } from '@/utils/fileTypeUtils';
import {
  documentStateFromMenuKey,
  documentStateMenuOption,
} from '@/components/file_vault/documentStateMenu';
import {
  ACCESS_PERMISSION_ADMIN,
  ACCESS_PERMISSION_WRITE,
  deniedReason,
} from '@/utils/accessPermission';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaSubPanel from '@/components/design/composite/GaiaSubPanel.vue';
import GaiaNotFound from '@/components/design/composite/GaiaNotFound.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth, gaiaNeutral } from '@/components/design/themes/tokens';
import ContractManagementExtractionDetailsModal from '@/components/contractManagement/ContractManagementExtractionDetailsModal.vue';
import ContractFileStaticModels from '@/components/contractManagement/ContractFileStaticModels.vue';
import ContractFileTermDates from '@/components/contractManagement/ContractFileTermDates.vue';
import ContractFileExtractions from '@/components/contractManagement/ContractFileExtractions.vue';
import MoveContractFileModal from '@/components/contractManagement/MoveContractFileModal.vue';
import VaultFileRevisionUploadModal from '@/components/file_vault/VaultFileRevisionUploadModal.vue';
import GaiaVaultCrumbs from '@/components/design/domain/fileVault/GaiaVaultCrumbs.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { askAiLimitWarning, askAiRoute } from '@/utils/askAi';
import { useDebounceFn } from '@vueuse/core';
import { useVaultStore } from '@/stores/vaultStore';
import { useAuthStore } from '@/stores/auth';
import { logger } from '@/utils/logger';
import { extractErrorDetail, isAccessDenied } from '@/utils/errorHandler';
import { safeStringify } from '@/utils/jsonHelpers';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useVaultWebSocket } from '@/composables/useVaultWebSocket';
import { contractRoute } from '@/router/links';
import { contractFolderRoute } from '@/services/vaultLocationService';
import { ContractsService } from '@/services/contractManagement/contractsService';

// ==================== Router & State ====================
const route = useRoute();
const router = useRouter();
const message = useGaiaToast();

function handleAskAi(): void {
  const contractFileUids = [contractFileUid.value].filter(Boolean);
  const limitWarning = askAiLimitWarning({ contractFileUids });
  if (limitWarning) {
    message.warning(limitWarning);
    return;
  }
  void router.push(askAiRoute({ contractFileUids }));
}
const vaultStore = useVaultStore();
const authStore = useAuthStore();

const contractFileUid = computed(() => route.params.uid as string);
const currentContractUid = computed(() => contractFile.value?.contract.uid ?? null);

// State
const contractFile = ref<ContractManagementContractFileInDbExtended | null>(null);
/** The backend refused the file: deleted, no access, or another organisation's. */
const unavailable = ref(false);
const loadingFile = ref<boolean>(false);

// ==================== Revision preview state ====================
const revisions = ref<VaultFileRevisionInDb[]>([]);
const selectedRevisionUid = ref<string | null>(null); // null before revisions load; current UID = no filter

const currentRevisionUid = computed(
  () => contractFile.value?.vault_file?.current_revision?.uid ?? null
);

// null when current version selected (no filter needed), actual UID otherwise
const filterRevisionUid = computed(() => {
  if (!selectedRevisionUid.value || selectedRevisionUid.value === currentRevisionUid.value) {
    return null;
  }
  return selectedRevisionUid.value;
});

const isPreviewing = computed(() => filterRevisionUid.value !== null);

const previewRevisionLabel = computed(() => {
  const r = revisions.value.find(x => x.uid === selectedRevisionUid.value);
  return r ? `v${r.version_number}` : '';
});

const revisionOptions = computed(() =>
  revisions.value.map(r => ({
    label: `v${r.version_number}`,
    value: r.uid,
  }))
);

const selectedRevision = computed(
  () => revisions.value.find(r => r.uid === selectedRevisionUid.value) ?? null
);

async function loadRevisions(): Promise<void> {
  if (!vaultFileUid.value) return;
  try {
    const res = await vaultStore.listRevisions(vaultFileUid.value, 50, 0);
    revisions.value = res.results;
    // Set initial selection to current revision (filterRevisionUid stays null — no refetch)
    if (currentRevisionUid.value) {
      selectedRevisionUid.value = currentRevisionUid.value;
    }
  } catch {
    // non-critical — selector just won't show older versions
  }
}

async function handleRevisionUploaded(): Promise<void> {
  // Re-fetch contract file to update current_revision (file name, version number, etc.)
  await fetchContractFile();
  // Reload revisions list and select the new current revision
  await loadRevisions();
  // Refresh extractions and static models for the new revision
  await Promise.all([fetchCoreStaticModels(), fetchExtractions()]);
}

// ==================== Static Models State (Lifted from child component) ====================
const obligations = ref<ContractManagementObligationInDb[]>([]);
const clauses = ref<ContractManagementClauseInDb[]>([]);
const contractualParties = ref<ContractualParty[]>([]);
const dates = ref<ContractManagementDateInDb[]>([]);
const loadingStaticModels = ref<boolean>(false);

// Watch the effective filter UID, not raw selection, to avoid refetch when selecting current
watch(filterRevisionUid, async (): Promise<void> => {
  if (unavailable.value) return;
  await Promise.all([fetchCoreStaticModels(), fetchExtractions()]);
});

// Reload PDF preview when the selected revision changes while preview is open
watch(selectedRevisionUid, () => {
  if (showDocumentPreview.value) {
    selectedPage.value = null;
    selectedExtraction.value = null;
    currentHighlights.value = [];
    pdfKey.value++;
  }
});

// ==================== Extractions State (Lifted from child component) ====================
const extractions = ref<ContractManagementExtractionInDb[]>([]);
const loadingExtractions = ref<boolean>(false);
const totalExtractions = ref<number>(0);
const originalTotalExtractions = ref<number>(0);
const extractionsLoaded = ref<boolean>(false);

// Extraction edit modal state
const showExtractionEditModal = ref<boolean>(false);
const editingExtraction = ref<{
  columnUid: string;
  extraction: ContractManagementExtractionInDb | null;
  column: ContractManagementViewColumnInDb | null;
  contractUid?: string;
  fileUid?: string;
} | null>(null);

// Edit/Delete state
const showEditModal = ref<boolean>(false);
const showMoveModal = ref<boolean>(false);
const showRevisionUploadModal = ref<boolean>(false);
const editFormData = ref<{
  /** Null until the AI has classified the file. */
  type: ContractManagementContractFileType | null;
  description: string;
}>({
  type: null,
  description: '',
});
const saving = ref<boolean>(false);
const deleting = ref<boolean>(false);
const downloading = ref<boolean>(false);

// PDF preview state
const showDocumentPreview = ref<boolean>(false);
const selectedPage = ref<number | null>(null);
const selectedExtraction = ref<ContractManagementExtractionInDb | null>(null);
const currentHighlights = ref<string[]>([]);
const pdfKey = ref(0);
const pdfPreviewRef = ref<InstanceType<typeof GaiaVaultFilePdfPreview>>();

// Split-pane drag fires @update:size on every pixel of movement. Without
// debouncing, each fire remounts GaiaVaultFilePdfPreview (via :key="pdfKey"),
// and each remount re-runs its immediate watcher + getVaultFileMetadata —
// hundreds of requests per drag. Debounce so the remount only happens once
// after the user stops dragging.
const handleSplitResize = useDebounceFn((): void => {
  pdfKey.value++;
}, 200);

// ==================== Computed Properties ====================

const contractName = computed(() => {
  return contractFile.value?.contract.name ?? '';
});

const vaultFileName = computed(() => {
  return (
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    selectedRevision.value?.file_name ||
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    contractFile.value?.vault_file?.current_revision?.file_name ||
    'Unknown File'
  );
});

const vaultFileUid = computed(() => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
  return contractFile.value?.vault_file?.uid || null;
});

const vaultFileVersionNumber = computed(
  () => contractFile.value?.vault_file?.current_revision?.version_number ?? null
);

const fileType = computed(() =>
  contractFile.value ? contractFileTypeLabel(contractFile.value.type) : ''
);

const contractFileDescription = computed(() => contractFile.value?.description ?? '');

/** The version picker: the dropdown label, and the menu behind it. */
const selectedRevisionLabel = computed(() => {
  const current = revisionOptions.value.find(
    option => option.value === (selectedRevisionUid.value ?? currentRevisionUid.value)
  );
  return current?.label ?? 'Current version';
});

const revisionMenuOptions = computed(() =>
  revisionOptions.value.map(option => ({ key: option.value, label: option.label }))
);

/**
 * The caller's bits: a contract file carries its contract's, on its vault file
 * (and on the contract in this response). Null — not computed — gates nothing.
 */
const fileBits = computed(
  () => contractFile.value?.vault_file?.permissions ?? contractFile.value?.contract.permissions
);
const writeBlock = computed(() => deniedReason(fileBits.value, ACCESS_PERMISSION_WRITE));

/** Disables a menu entry the bits rule out, with the reason as its title. */
function gateOption(option: Record<string, unknown>, flag: number): Record<string, unknown> {
  const reason = deniedReason(fileBits.value, flag);
  if (!reason) return option;
  const props = (option.props as Record<string, unknown> | undefined) ?? {};
  return { ...option, disabled: true, props: { ...props, title: reason } };
}

/**
 * Everything but signing lives behind the ⋯. Reading the document and taking a
 * copy of it lead — they are the two you reach for most — then the rest of the
 * file's own actions, with the destructive one alone past a divider.
 */
const headerMenuOptions = computed((): Record<string, unknown>[] => {
  const items: Record<string, unknown>[] = [
    { key: 'preview', label: showDocumentPreview.value ? 'Hide preview' : 'Show preview' },
    { key: 'download', label: downloading.value ? 'Downloading…' : 'Download' },
    { key: 'divider-view', type: 'divider' },
    contractFile.value?.vault_file?.current_revision?.kind === VaultFileRevisionKind.TEMPLATED
      ? {
          key: 'ask-ai',
          label: 'Ask AI',
          disabled: true,
          props: { title: 'A draft has no text for the AI to read until it is saved.' },
        }
      : { key: 'ask-ai', label: 'Ask AI' },
  ];
  if (vaultFileUid.value) {
    items.push(
      gateOption({ key: 'add-revision', label: 'Add revision' }, ACCESS_PERMISSION_WRITE),
      { key: 'versions', label: 'Manage versions' }
    );
    // Sets the state of the revision being read, as the chip by the title shows it.
    // Appendices take no part in the contract's state, so there is none to set,
    // and a draft revision is always "In draft" (the backend refuses a change).
    if (
      selectedRevision.value &&
      selectedRevision.value.kind !== VaultFileRevisionKind.TEMPLATED &&
      fileTypeHasState(contractFile.value?.type)
    ) {
      items.push(
        gateOption(
          documentStateMenuOption(selectedRevision.value.document_state),
          ACCESS_PERMISSION_WRITE
        )
      );
    }
    // As in the vault: the file's current revision, PDFs only, held while a
    // conversion runs.
    const current = contractFile.value?.vault_file?.current_revision;
    if (current && isPdfFile(current.mime_type, current.file_name)) {
      const converting = isConversionRunning(current.conversion_status);
      items.push(
        gateOption(
          {
            key: 'convert-to-docx',
            label: converting ? 'Conversion in progress' : 'Convert to DOCX',
            disabled: converting,
          },
          ACCESS_PERMISSION_WRITE
        )
      );
    }
  }
  items.push(
    gateOption({ key: 'edit', label: 'Edit details' }, ACCESS_PERMISSION_WRITE),
    gateOption({ key: 'move', label: 'Move to another contract' }, ACCESS_PERMISSION_ADMIN),
    { key: 'divider', type: 'divider' },
    gateOption(
      { key: 'delete', label: 'Delete file', props: gaiaDangerOption },
      ACCESS_PERMISSION_ADMIN
    )
  );
  return items;
});

function onHeaderMenu(key: string): void {
  const documentState = documentStateFromMenuKey(key);
  if (documentState !== undefined) {
    void setDocumentState(documentState);
    return;
  }
  switch (key) {
    case 'preview':
      handlePreview();
      return;
    case 'download':
      void handleDownload();
      return;
    case 'ask-ai':
      handleAskAi();
      return;
    case 'convert-to-docx':
      void convertToDocx();
      return;
    case 'add-revision':
      showRevisionUploadModal.value = true;
      return;
    case 'versions':
      void router.push({ name: 'vault-file-versions', params: { uid: vaultFileUid.value } });
      return;
    case 'edit':
      handleEdit();
      return;
    case 'move':
      handleMove();
      return;
    case 'delete':
      confirmDelete.value = true;
  }
}

/** Sets the lifecycle state of the selected revision. */
async function setDocumentState(state: DocumentState): Promise<void> {
  const revision = selectedRevision.value;
  if (!vaultFileUid.value || !revision) return;
  try {
    const updated = await vaultStore.updateRevision(vaultFileUid.value, revision.uid, {
      document_state: state,
    });
    revisions.value = revisions.value.map(r => (r.uid === updated.uid ? updated : r));
  } catch (error) {
    logger.error('Failed to update the document state:', error);
    // The backend refuses some transitions (422) and says why.
    message.error(extractErrorDetail(error, 'Failed to update the state'));
  }
}

/**
 * Starts the PDF → DOCX conversion, then reloads the file quietly (no header
 * shimmer) so the menu shows it as in progress.
 */
async function convertToDocx(): Promise<void> {
  if (!vaultFileUid.value) return;
  try {
    const result = await vaultStore.convertPdfToDocx(vaultFileUid.value);
    message.success(result.message || `Started converting ${vaultFileName.value} to DOCX`);
    contractFile.value = await ContractFileDetailService.getContractFile(contractFileUid.value);
  } catch (error) {
    logger.error('Failed to convert file to DOCX:', error);
    message.error(
      `Failed to convert file to DOCX: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

const confirmDelete = ref<boolean>(false);

function confirmDeleteNow(): void {
  confirmDelete.value = false;
  void handleDelete();
}
const revisionChangeDescription = computed(() => selectedRevision.value?.change_description ?? '');

const createdAt = computed(() => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
  const date = selectedRevision.value?.created_at || contractFile.value?.created_at;
  return date ? formatDate(date) : '—';
});

const updatedAt = computed(() => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
  const date = selectedRevision.value?.updated_at || contractFile.value?.updated_at;
  return date ? formatDate(date) : '—';
});

// ==================== Data Fetching ====================

async function fetchContractFile(): Promise<void> {
  loadingFile.value = true;
  try {
    contractFile.value = await ContractFileDetailService.getContractFile(contractFileUid.value);
  } catch (error) {
    if (isAccessDenied(error)) {
      // An answer, not a failure: the page renders "not available" instead.
      logger.warn('Contract file not available:', error);
      unavailable.value = true;
      return;
    }
    logger.error('Error fetching contract file:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch contract file';
    message.error(errorMessage);
  } finally {
    loadingFile.value = false;
  }
}

// ==================== Static Models Data Fetching (Lifted from ContractFileStaticModels) ====================

/**
 * Fetch all static models for the contract file
 * This is called once on mount and after any mutations
 */
async function fetchCoreStaticModels(): Promise<void> {
  loadingStaticModels.value = true;
  const revUid = filterRevisionUid.value;
  try {
    const uid = contractFileUid.value;
    // Parties link to the file itself, so they don't follow the revision filter.
    const [obligationsData, clausesData, { data: partiesData }, datesData] = await Promise.all([
      revUid
        ? ContractFileDetailService.getObligationsByRevision(uid, revUid)
        : ContractFileDetailService.getObligations(uid),
      ContractFileDetailService.getClauses(uid, revUid),
      getContractManagementContractFileContractualParties({ path: { uid }, throwOnError: true }),
      ContractFileDetailService.getDates(uid, revUid),
    ]);
    obligations.value = obligationsData;
    clauses.value = clausesData;
    contractualParties.value = partiesData;
    dates.value = datesData;
  } catch (error) {
    logger.error('Error fetching static models:', error);
    message.error(error instanceof Error ? error.message : 'Failed to fetch static models');
  } finally {
    loadingStaticModels.value = false;
  }
}

// ==================== Extractions Data Fetching (Lifted from ContractFileExtractions) ====================

/**
 * Fetch extractions for the contract file
 * This is called once on mount and can be triggered by child components
 */
async function fetchExtractions(searchParams?: {
  text?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
}): Promise<void> {
  loadingExtractions.value = true;
  try {
    const result: ContractManagementExtractionSearchResult =
      await ContractFileDetailService.searchExtractions(contractFileUid.value, {
        text: searchParams?.text ?? '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        page: searchParams?.page || 1,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        pageSize: searchParams?.pageSize || 10,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sortKey: searchParams?.sortKey || 'created_at',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sortDirection: searchParams?.sortDirection || 'desc',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        revisionUid: filterRevisionUid.value || null,
      });

    extractions.value = result.results;
    totalExtractions.value = result.result_count;

    // Update original total only if we're not searching
    if (!searchParams?.text || searchParams.text.trim() === '') {
      originalTotalExtractions.value = result.result_count;
    }

    extractionsLoaded.value = true;
  } catch (error) {
    logger.error('Error fetching extractions:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch extractions';
    message.error(errorMessage);
  } finally {
    loadingExtractions.value = false;
  }
}

// ==================== Date Formatting ====================

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ==================== Handlers ====================

// ==================== Edit/Delete/Download Handlers ====================

// Options for file type select
const fileTypeOptions = enumToSelectOptionsWithLabels(ContractManagementContractFileType);

// Form validation rules

function handleEdit(): void {
  if (!contractFile.value) return;

  editFormData.value = {
    type: contractFile.value.type,
    description: contractFile.value.description ?? '',
  };
  showEditModal.value = true;
}

async function handleSaveEdit(): Promise<void> {
  if (!contractFile.value) return;

  saving.value = true;
  try {
    const updateData: ContractManagementContractFileUpdateUser = {
      // A PUT cannot clear the type; while unclassified it is left out.
      type: editFormData.value.type ?? undefined,
      description: editFormData.value.description.trim() || null,
    };

    await ContractFilesService.update(contractFile.value.uid, updateData);
    message.success('Contract file updated successfully');
    showEditModal.value = false;

    // Refresh contract file data
    await fetchContractFile();
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to update contract file';
    message.error(errorMsg);
  } finally {
    saving.value = false;
  }
}

function handleCancelEdit(): void {
  showEditModal.value = false;
  editFormData.value = { type: null, description: '' };
}

function handleMove(): void {
  showMoveModal.value = true;
}

/** Why this file can't go out for signing; null when it can — a draft must be finished first. */
const signBlock = computed<string | null>(() => {
  if (writeBlock.value) return writeBlock.value;
  return contractFile.value?.vault_file?.current_revision?.kind === VaultFileRevisionKind.TEMPLATED
    ? 'Finish the draft before sending it for signing.'
    : null;
});

function handleSignThis(): void {
  if (!currentContractUid.value) {
    message.warning('The parent contract is not available for signature preparation yet.');
    return;
  }

  void router.push({
    name: 'signing-contract-request-new',
    params: { uid: currentContractUid.value },
    query: {
      contract_file_uid: contractFileUid.value,
    },
  });
}

async function handleMoved(
  _contractFileUid: string,
  updatedFile?: ContractManagementContractFileInDb & { contract?: ContractManagementContractInDb },
  targetContract?: ContractManagementContractInDb
): Promise<void> {
  // Resolve contract for File Information display: modal always passes targetContract (existing = selected contract, new = display contract or API response)
  const displayContract = targetContract ?? updatedFile?.contract;

  if (updatedFile && displayContract) {
    // Merge updated file with display contract so contractName (contractFile.value.contract.name) updates
    contractFile.value = { ...updatedFile, contract: displayContract };
  } else {
    await fetchContractFile();
  }
}

async function handleDelete(): Promise<void> {
  if (!contractFile.value) return;

  // Read before the await: the record is gone once the delete resolves. The
  // contract's folder too — deleting its last file deletes the contract.
  const contractUid = contractFile.value.contract.uid;
  deleting.value = true;
  try {
    const landing = await contractFolderRoute('contract_file', contractFile.value.uid);
    await ContractFilesService.delete(contractFile.value.uid);
    const contractRemains = await ContractsService.getById(contractUid).then(
      () => true,
      () => false
    );
    message.success(contractRemains ? 'Contract file deleted' : 'File and contract deleted');
    void router.push(contractRemains ? contractRoute(contractUid) : landing);
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to delete contract file';
    message.error(errorMsg);
  } finally {
    deleting.value = false;
  }
}

async function handleDownload(): Promise<void> {
  const revision = selectedRevision.value;
  if (!revision) {
    message.warning('No file version available for download.');
    return;
  }

  downloading.value = true;
  try {
    const contentType = revision.mime_type || 'application/octet-stream';
    const blob = await ContractFileDownloadService.downloadVaultFile(revision.uid, contentType);
    const fileName =
      revision.file_name ||
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      contractFile.value?.description ||
      `contract-file-${contractFileUid.value.slice(0, 8)}`;
    ContractFileDownloadService.triggerDownload(blob, fileName);
    message.success('File downloaded successfully');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to download file';
    message.error(errorMessage);
  } finally {
    downloading.value = false;
  }
}

function handlePreview(): void {
  if (showDocumentPreview.value) {
    showDocumentPreview.value = false;
    return;
  }
  if (!vaultFileUid.value) {
    message.warning('Vault file not available for preview');
    return;
  }
  showDocumentPreview.value = true;
  pdfKey.value++;
}

// ==================== Extraction Edit Handlers ====================

/**
 * Open extraction edit modal
 * The modal expects extraction data in a specific format
 */
// Get highlight text from extraction
// Uses highlighted_text if available, otherwise falls back to value as string
function getHighlightTextFromExtraction(
  extraction: ContractManagementExtractionInDb | null
): string[] {
  if (!extraction) return [];

  const highlightTexts: string[] = [];

  // Prioritize highlighted_text (from AI or human overwrite)
  const highlightedText =
    extraction.human_overwrite?.highlighted_text === undefined
      ? extraction.highlighted_text
      : extraction.human_overwrite.highlighted_text;

  if (highlightedText && Array.isArray(highlightedText)) {
    // Filter out empty strings and ensure reasonable length
    const validTexts = highlightedText
      .filter(text => text && typeof text === 'string' && text.trim().length >= 3)
      .map(text => text.trim())
      .slice(0, 10); // Limit to 10 texts to avoid performance issues

    highlightTexts.push(...validTexts);
  }

  // Fallback: use value as string if no highlighted_text available
  if (highlightTexts.length === 0) {
    const value =
      extraction.human_overwrite?.value === undefined
        ? extraction.value
        : extraction.human_overwrite.value;

    if (value !== null) {
      // Convert value to string, handling different types
      let valueStr = '';
      if (typeof value === 'string') {
        valueStr = value;
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        valueStr = String(value);
      } else if (Array.isArray(value)) {
        valueStr = value.map(v => safeStringify(v)).join(' ');
      } else if (typeof value === 'object') {
        valueStr = JSON.stringify(value);
      }

      // Only add if it's a reasonable length
      const cleanValue = valueStr.trim();
      if (cleanValue.length >= 3 && cleanValue.length <= 200) {
        highlightTexts.push(cleanValue);
      }
    }
  }

  return highlightTexts;
}

// Handle page click from extractions
function handlePageClick(page: number, extraction: ContractManagementExtractionInDb): void {
  if (!vaultFileUid.value) {
    message.warning('Vault file not available for preview');
    return;
  }

  selectedPage.value = page;
  selectedExtraction.value = extraction;
  showDocumentPreview.value = true;

  // Navigate to page after PDF loads
  void nextTick(() => {
    if (pdfPreviewRef.value) {
      showInPdf(page);
    }
  });

  // Force PDF reload when opening preview
  pdfKey.value++;
}

// Show page in PDF with highlighting
function showInPdf(page: number): void {
  if (pdfPreviewRef.value) {
    selectedPage.value = page;
    pdfPreviewRef.value.goToPage(page);

    // Only highlight if this page matches the extraction's page
    const shouldHighlight = isPageMatchingExtraction(page, selectedExtraction.value);

    if (shouldHighlight) {
      // Apply highlights for the selected extraction
      const highlightText = getHighlightTextFromExtraction(selectedExtraction.value);
      const validHighlightText = highlightText.filter(text => text && text.trim().length > 0);

      if (validHighlightText.length > 0) {
        currentHighlights.value = validHighlightText;

        // Set highlight text after a delay to ensure page is loaded
        setTimeout(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText(validHighlightText);
          }
        }, 500);
      } else {
        // Clear highlights if no valid text
        currentHighlights.value = [];
        void nextTick(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText([]);
          }
        });
      }
    } else {
      // Clear highlights if page doesn't match extraction's page
      currentHighlights.value = [];
      void nextTick(() => {
        if (pdfPreviewRef.value) {
          pdfPreviewRef.value.setHighlightText([]);
        }
      });
    }
  }
}

// Handle document load
function handleDocumentLoad(): void {
  if (selectedPage.value && pdfPreviewRef.value) {
    void nextTick(() => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
      const page = selectedPage.value || 1;
      pdfPreviewRef.value?.goToPage(page);

      // Apply highlights after page navigation, only if page matches extraction's page
      setTimeout(() => {
        if (pdfPreviewRef.value && selectedPage.value) {
          const shouldHighlight = isPageMatchingExtraction(
            selectedPage.value,
            selectedExtraction.value
          );

          if (shouldHighlight) {
            const highlightText = getHighlightTextFromExtraction(selectedExtraction.value);
            const validHighlightText = highlightText.filter(text => text && text.trim().length > 0);

            if (validHighlightText.length > 0) {
              pdfPreviewRef.value.setHighlightText(validHighlightText);
              currentHighlights.value = validHighlightText;
            } else {
              pdfPreviewRef.value.setHighlightText([]);
              currentHighlights.value = [];
            }
          } else {
            pdfPreviewRef.value.setHighlightText([]);
            currentHighlights.value = [];
          }
        }
      }, 500); // Delay to ensure page and text layer are fully rendered
    });
  }
}

// Handle close document preview
function handleClosePreview(): void {
  showDocumentPreview.value = false;
  selectedPage.value = null;
  selectedExtraction.value = null;
  currentHighlights.value = [];

  // Clear highlights in PDF component
  if (pdfPreviewRef.value) {
    pdfPreviewRef.value.clearHighlights();
  }
}

// Handle highlights cleared
function handleHighlightsCleared(): void {
  currentHighlights.value = [];
}

// Check if a page matches the extraction's page
function isPageMatchingExtraction(
  page: number,
  extraction: ContractManagementExtractionInDb | null
): boolean {
  if (!extraction) return false;

  // Get the extraction's page (from human overwrite or original)
  const extractionPage =
    extraction.human_overwrite?.page === undefined
      ? extraction.page
      : extraction.human_overwrite.page;

  // Check if the current page matches the extraction's page
  return extractionPage !== null && extractionPage === page;
}

// Handle PDF page change
function handlePdfPageChange(page: number): void {
  selectedPage.value = page;

  // Update highlights when page changes (only if we have a selected extraction)
  if (pdfPreviewRef.value && selectedExtraction.value) {
    // Only highlight if this page matches the extraction's page
    const shouldHighlight = isPageMatchingExtraction(page, selectedExtraction.value);

    if (shouldHighlight) {
      const highlightText = getHighlightTextFromExtraction(selectedExtraction.value);
      const validHighlightText = highlightText.filter(text => text && text.trim().length > 0);

      if (validHighlightText.length > 0) {
        // Apply highlights for this page with a delay to ensure text layer is ready
        setTimeout(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText(validHighlightText);
            currentHighlights.value = validHighlightText;
          }
        }, 300);
      } else {
        // Clear highlights if no valid text
        currentHighlights.value = [];
        void nextTick(() => {
          if (pdfPreviewRef.value) {
            pdfPreviewRef.value.setHighlightText([]);
          }
        });
      }
    } else {
      // Clear highlights if page doesn't match extraction's page
      currentHighlights.value = [];
      void nextTick(() => {
        if (pdfPreviewRef.value) {
          pdfPreviewRef.value.setHighlightText([]);
        }
      });
    }
  }
}

function handleEditExtraction(extraction: ContractManagementExtractionInDb): void {
  // Create a minimal column object since we don't have full column data in this view
  // The modal will work with just the extraction data
  const columnData: ContractManagementViewColumnInDb | null = {
    uid: extraction.uid, // Use extraction uid as column uid
    name: extraction.prompt, // Use prompt as column name
    extraction_prompt: {
      prompt: extraction.prompt,
      response_type: extraction.response_type,
      example_data: extraction.example_data,
    },
  } as ContractManagementViewColumnInDb;

  editingExtraction.value = {
    columnUid: extraction.uid,
    extraction,
    column: columnData,
    contractUid: contractFile.value?.contract.uid,
    fileUid: contractFile.value?.uid,
  };

  showExtractionEditModal.value = true;
}

/**
 * Handle extraction update - refresh the extractions list
 */
async function handleExtractionUpdate(): Promise<void> {
  // Refetch extractions after update
  await fetchExtractions();
  showExtractionEditModal.value = false;
}

/**
 * Handle static models refresh (called by child component after mutations)
 */
async function handleStaticModelsRefresh(): Promise<void> {
  await fetchCoreStaticModels();
}

// ==================== Live updates ====================

/**
 * Re-read the file and its versions without the header shimmer, keeping the
 * picked version — a version's effective state follows its dates, so the
 * state chip reads off the fresh list.
 */
async function refreshFileQuietly(): Promise<void> {
  const vaultUid = vaultFileUid.value;
  if (!vaultUid) return;
  try {
    const [file, { results }] = await Promise.all([
      ContractFileDetailService.getContractFile(contractFileUid.value),
      vaultStore.listRevisions(vaultUid, 50, 0),
    ]);
    contractFile.value = file;
    revisions.value = results;
  } catch (error) {
    logger.error('Error refreshing contract file:', error);
  }
}

/** Finished extraction states — anything else is still on its way. */
const SETTLED = new Set<unknown>([TaskStatus.DONE, TaskStatus.FAILED, TaskStatus.PARTIAL]);

// The backend sends `vault_file_updated` on any change to the file's state —
// a date added, edited or deleted, from here or another tab — and a model
// extraction event as each built-in extraction settles. Both name the vault
// file, not a version, so the open version is simply re-read.
useVaultWebSocket({
  fileUids: () => (vaultFileUid.value ? [vaultFileUid.value] : []),
  onFileUpdated: () => {
    void Promise.all([refreshFileQuietly(), fetchCoreStaticModels()]);
  },
  onFileModelExtractionUpdated: (_uid, data) => {
    if (!SETTLED.has(data.status)) return;
    // Classification sets the file's type; every other task fills the static models.
    if (data.task_type === 'contract_management_classify_contract_file') {
      void refreshFileQuietly();
    } else {
      void fetchCoreStaticModels();
    }
  },
});

// ==================== Lifecycle ====================

onMounted(async () => {
  await fetchContractFile();
  // Everything below hangs off the file; a refused file means refused children.
  if (unavailable.value) return;
  // Load vault file revisions for the revision selector (non-critical)
  await loadRevisions();
  // Fetch static models and extractions once on mount
  await Promise.all([fetchCoreStaticModels(), fetchExtractions()]);
});
// --- chrome ----------------------------------------------------------------
const cardBorder = gaiaNeutral.borderCard;
const stripSurface = gaiaNeutral.surfaceChild;
const dividerColor = gaiaNeutral.borderDivider;
const shimmerBase = gaiaNeutral.surfaceSoft;
const shimmerHighlight = gaiaNeutral.surfaceMuted;
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.record">
    <template #breadcrumb>
      <n-breadcrumb-item v-if="unavailable">{{ navLabels.fileVault }}</n-breadcrumb-item>
      <template v-else>
        <!-- The trail ends with the file's contract. -->
        <gaia-vault-crumbs kind="contract_file" :uid="contractFileUid" />
        <n-breadcrumb-item>{{ vaultFileName }}</n-breadcrumb-item>
      </template>
    </template>
    <!-- The file is gone, or not visible from the active organisation. -->
    <gaia-not-found v-if="unavailable" />
    <n-flex v-else vertical :size="16">
      <!-- Header: the file, the version being looked at, and the actions. -->
      <n-flex align="flex-start" :size="20" :wrap="false">
        <n-flex vertical :size="7" :style="{ minWidth: 0 }">
          <gaia-text variant="label" size="nano" tone="faint">Contract file</gaia-text>
          <div v-if="loadingFile" class="shimmer title" />
          <n-flex v-else align="center" :size="12" :wrap="true">
            <gaia-header :level="1" variant="display" size="page" :style="{ margin: 0 }">
              {{ vaultFileName }}
            </gaia-header>
            <!-- The state of the version being read, as the contract header shows
                 the contract's. -->
            <gaia-contract-state-cell
              v-if="selectedRevision && fileTypeHasState(contractFile?.type)"
              :state="selectedRevision.effective_state"
              :reasons="selectedRevision.needs_attention_reasons"
              :failure-reason="selectedRevision.signature_failure_reason ?? null"
              :signature-infos="selectedRevision.signature_infos ?? null"
            />
          </n-flex>
        </n-flex>

        <n-flex
          align="center"
          :size="8"
          :wrap="false"
          data-tour="cm.file-actions"
          :style="{ marginLeft: 'auto', flex: 'none' }"
        >
          <!-- Which revision the page is reading. Picking an older one puts the
               page into preview mode, which the banner below announces. -->
          <gaia-dropdown
            v-if="revisionOptions.length > 1"
            :options="revisionMenuOptions"
            placement="bottom-end"
            @select="(key: string) => (selectedRevisionUid = key)"
          >
            <gaia-button size="medium">
              {{ selectedRevisionLabel }}
              <template #icon>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                >
                  <path d="M2.5 4.5 6 8l3.5-3.5" />
                </svg>
              </template>
            </gaia-button>
          </gaia-dropdown>

          <!-- Signing is the one thing this page exists to start, so it is the
               only action with a primary. Preview and download are how you read
               the file, not what you do to it — they sit in the ⋯ below. -->
          <gaia-button
            v-if="authStore.userPermissions.can_use_signing"
            type="primary"
            size="medium"
            :disabled="signBlock !== null"
            :title="signBlock ?? undefined"
            @click="handleSignThis"
          >
            <template #icon>
              <svg
                width="15"
                height="15"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M2.5 13.5h11" />
                <path
                  d="M3.8 10.8c1.4 0 1.8-6.3 3.2-6.3 1 0 .6 4.6.1 5.6-.3.6.4.9 1.1.3.8-.7 1.4-1.6 2.2-1.6.9 0 .6 1.6 1.8 1.6"
                />
              </svg>
            </template>
            Sign this
          </gaia-button>

          <gaia-dropdown
            :options="headerMenuOptions"
            placement="bottom-end"
            @select="(key: string) => onHeaderMenu(key)"
          >
            <gaia-icon-button :dimension="36" title="More actions">
              <n-icon :component="IconMore" :size="18" />
            </gaia-icon-button>
          </gaia-dropdown>
        </n-flex>
      </n-flex>

      <!-- Reading an older revision is a mode, not a filter: say so, and give
           the way back rather than leaving the reader to notice. -->
      <gaia-banner v-if="isPreviewing" plain type="info">
        Previewing entities from {{ previewRevisionLabel }} — the current version is v{{
          vaultFileVersionNumber
        }}.
        <template #action>
          <gaia-button text size="tiny" @click="selectedRevisionUid = currentRevisionUid">
            Back to current
          </gaia-button>
        </template>
      </gaia-banner>

      <!-- Meta strip: what this file is, in one line. -->
      <div class="meta-strip">
        <template v-if="loadingFile">
          <div class="shimmer" :style="{ width: '60%', margin: '11px 14px' }" />
        </template>
        <template v-else>
          <div class="meta-item">
            <gaia-text variant="label" size="nano" tone="faint">Contract</gaia-text>
            <gaia-link
              :style="{ fontWeight: 600 }"
              :to="contractFile ? contractRoute(contractFile.contract.uid) : undefined"
            >
              {{ contractName || '—' }}
            </gaia-link>
          </div>
          <span class="meta-divider" />

          <div class="meta-item">
            <gaia-text variant="label" size="nano" tone="faint">Kind</gaia-text>
            <gaia-tag variant="muted">{{ fileType }}</gaia-tag>
          </div>
          <span class="meta-divider" />

          <div class="meta-item">
            <gaia-text variant="label" size="nano" tone="faint">Created</gaia-text>
            <gaia-text size="control" tone="text" :style="{ fontVariantNumeric: 'tabular-nums' }">
              {{ createdAt }}
            </gaia-text>
          </div>
          <span class="meta-divider" />

          <div class="meta-item">
            <gaia-text variant="label" size="nano" tone="faint">Updated</gaia-text>
            <gaia-text size="control" tone="text" :style="{ fontVariantNumeric: 'tabular-nums' }">
              {{ updatedAt }}
            </gaia-text>
          </div>
        </template>
      </div>

      <!-- The long-form fields are prose, not row values: a label column
           beside them squeezes the measure and leaves the text floating with
           nothing to sit on. Each gets a card with its label above it. The
           term dates stack in the right quarter; the grid row stretches both
           sides to the taller one, so the description grows at the bottom or
           the two date panels share the extra height. One column when narrow
           (measured against the grid's own width). -->
      <n-grid cols="1 800:4" :x-gap="12" :y-gap="12">
        <n-gi span="1 800:3">
          <n-flex vertical :size="12" :wrap="false" :style="{ height: '100%' }">
            <gaia-sub-panel label="Description" :style="{ flex: 1 }">
              <gaia-text
                v-if="contractFileDescription"
                size="body"
                tone="text"
                :style="{ lineHeight: 1.6 }"
              >
                {{ contractFileDescription }}
              </gaia-text>
              <gaia-text v-else size="control" tone="faint">No description</gaia-text>
            </gaia-sub-panel>

            <gaia-sub-panel v-if="revisionChangeDescription" label="What changed">
              <gaia-text size="body" tone="text" :style="{ lineHeight: 1.6 }">
                {{ revisionChangeDescription }}
              </gaia-text>
            </gaia-sub-panel>
          </n-flex>
        </n-gi>
        <n-gi>
          <ContractFileTermDates
            :dates="dates"
            :loading="loadingStaticModels"
            :contract-file-uid="contractFileUid"
            :revision-uid="filterRevisionUid"
            @refresh="handleStaticModelsRefresh"
          />
        </n-gi>
      </n-grid>
    </n-flex>

    <!-- Normal View: Extractions first, Static Models at the bottom -->
    <div v-if="!unavailable && !showDocumentPreview" class="content-container">
      <ContractFileExtractions
        data-tour="cm.file-extractions"
        :contract-file-uid="contractFileUid"
        :extractions="extractions"
        :total-extractions="totalExtractions"
        :original-total-extractions="originalTotalExtractions"
        :loading="loadingExtractions"
        :extractions-loaded="extractionsLoaded"
        @edit-extraction="handleEditExtraction"
        @page-click="handlePageClick"
        @open-document="handlePreview"
        @fetch-extractions="fetchExtractions"
      />

      <ContractFileStaticModels
        :contract-file-uid="contractFileUid"
        :obligations="obligations"
        :clauses="clauses"
        :contractual-parties="contractualParties"
        :loading="loadingStaticModels"
        @refresh="handleStaticModelsRefresh"
      />
    </div>

    <!-- PDF Preview Split View: Only Static Models and Extractions -->
    <NSplit
      v-if="!unavailable && showDocumentPreview && vaultFileUid"
      class="main-split-view"
      :max="0.75"
      :min="0.35"
      :default-size="0.45"
      @update:size="handleSplitResize"
    >
      <!-- Left Panel: Static Models and Extractions -->
      <template #1>
        <div class="left-panel">
          <div class="left-panel-content">
            <!-- Extractions -->
            <ContractFileExtractions
              data-tour="cm.file-extractions"
              :contract-file-uid="contractFileUid"
              :extractions="extractions"
              :total-extractions="totalExtractions"
              :original-total-extractions="originalTotalExtractions"
              :loading="loadingExtractions"
              :extractions-loaded="extractionsLoaded"
              :is-split-view="true"
              @edit-extraction="handleEditExtraction"
              @page-click="handlePageClick"
              @open-document="handlePreview"
              @fetch-extractions="fetchExtractions"
            />

            <!-- Static Models -->
            <ContractFileStaticModels
              :contract-file-uid="contractFileUid"
              :obligations="obligations"
              :clauses="clauses"
              :contractual-parties="contractualParties"
              :loading="loadingStaticModels"
              @refresh="handleStaticModelsRefresh"
            />
          </div>
        </div>
      </template>

      <!-- Right Panel: PDF Preview -->
      <template #2>
        <div class="right-panel">
          <div class="pdf-panel-header">
            <div class="pdf-header-content">
              <div>
                <NText strong style="font-size: 16px">Document Preview</NText>
                <NText
                  v-if="vaultFileName"
                  depth="3"
                  style="font-size: 13px; display: block; margin-top: 4px"
                >
                  {{ vaultFileName }}
                </NText>
              </div>
              <NButton
                quaternary
                circle
                size="small"
                title="Close document preview"
                @click="handleClosePreview"
              >
                <template #icon>
                  <NIcon><IconClose /></NIcon>
                </template>
              </NButton>
            </div>
          </div>
          <div class="pdf-container-wrapper">
            <gaia-vault-file-pdf-preview
              v-if="vaultFileUid"
              ref="pdfPreviewRef"
              :key="pdfKey"
              :vault-file-uid="vaultFileUid"
              :revision-uid="selectedRevisionUid || undefined"
              :revision-preview-status="selectedRevision?.preview_status ?? undefined"
              @loaded="handleDocumentLoad"
              @highlights-cleared="handleHighlightsCleared"
              @page-change="handlePdfPageChange"
            />
          </div>
        </div>
      </template>
    </NSplit>

    <MoveContractFileModal
      v-model:show="showMoveModal"
      :contract-file-uid="contractFileUid"
      @moved="handleMoved"
    />

    <gaia-modal
      :show="showEditModal"
      width="460px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && handleCancelEdit()"
    >
      <gaia-text size="subtitle" weight="semibold" tone="ink">Edit contract file</gaia-text>

      <n-flex vertical :size="14" :style="{ marginTop: '16px' }">
        <gaia-form-field label="File type" required>
          <gaia-select
            :value="editFormData.type"
            :options="fileTypeOptions"
            placeholder="Not classified yet"
            style="width: 100%"
            @update:value="
              (value: ContractManagementContractFileType) => (editFormData.type = value)
            "
          />
        </gaia-form-field>

        <gaia-form-field label="Description" hint="Optional">
          <gaia-textarea
            :value="editFormData.description"
            :autosize="{ minRows: 3 }"
            style="width: 100%"
            @update:value="(value: string) => (editFormData.description = value)"
          />
        </gaia-form-field>
      </n-flex>

      <n-flex justify="end" :size="8" :style="{ marginTop: '18px' }">
        <gaia-button size="small" @click="handleCancelEdit">Cancel</gaia-button>
        <gaia-button type="primary" size="small" :loading="saving" @click="handleSaveEdit">
          {{ saving ? 'Saving…' : 'Save changes' }}
        </gaia-button>
      </n-flex>
    </gaia-modal>

    <!-- Deleting the file acts on the whole page, so it stays a dialog. -->
    <gaia-modal
      :show="confirmDelete"
      width="440px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && (confirmDelete = false)"
    >
      <gaia-text size="subtitle" weight="semibold" tone="ink">
        Delete “{{ vaultFileName }}”?
      </gaia-text>
      <gaia-text
        size="body"
        tone="tertiary"
        :style="{ display: 'block', marginTop: '10px', lineHeight: 1.55 }"
      >
        The document is deleted with all of its versions and extractions. If it is the contract's
        last file, the contract is deleted too.
      </gaia-text>
      <n-flex justify="end" :size="8" :style="{ marginTop: '20px' }">
        <gaia-button size="small" @click="confirmDelete = false">Cancel</gaia-button>
        <gaia-button type="error" size="small" :loading="deleting" @click="confirmDeleteNow">
          Delete file
        </gaia-button>
      </n-flex>
    </gaia-modal>

    <ContractManagementExtractionDetailsModal
      v-if="editingExtraction"
      v-model:show="showExtractionEditModal"
      :cell-data="editingExtraction"
      :on-data-update="handleExtractionUpdate"
    />

    <VaultFileRevisionUploadModal
      v-if="vaultFileUid"
      v-model:show="showRevisionUploadModal"
      :file-uid="vaultFileUid"
      :file-name="vaultFileName"
      @uploaded="handleRevisionUploaded"
    />
  </Layout>
</template>

<style scoped>
/* The meta strip: one line that answers "what is this file". Wraps rather than
   scrolls, because a wrapped fact is still readable and a clipped one is not. */
.meta-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 0 4px;
  border: 1px solid v-bind(cardBorder);
  border-radius: 10px;
  background: v-bind(stripSurface);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 11px 14px;
}

.meta-divider {
  width: 1px;
  height: 18px;
  background: v-bind(dividerColor);
}

/* Side by side while both fit, stacked once one would drop under ~320px —
   auto-fit is the whole rule, so there is no breakpoint to keep in sync. */
.shimmer {
  height: 11px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    v-bind(shimmerHighlight) 0%,
    v-bind(shimmerBase) 45%,
    v-bind(shimmerHighlight) 90%
  );
  background-size: 480px 100%;
  animation: file-detail-shimmer 1.5s linear infinite;
}

.shimmer.title {
  width: 420px;
  height: 26px;
  border-radius: 6px;
}

@keyframes file-detail-shimmer {
  0% {
    background-position: -280px 0;
  }

  100% {
    background-position: 480px 0;
  }
}

.page-header {
  margin-bottom: 32px;
  background: transparent;
}

.header-title {
  display: flex;
  align-items: center;
}

.header-title-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.content-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin: 24px 0;
}

/* File Info Strip */
.file-info-strip {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.file-info-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
}

.file-info-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}

.file-info-label {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.file-info-value {
  font-size: 13px;
  color: #374151;
}

.file-info-link {
  color: #2563eb;
  font-weight: 500;
}

.file-info-filename {
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-info-description-row {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  padding-top: 7px;
  margin-top: 7px;
  border-top: 1px solid #f1f5f9;
}

/* NTooltip renders an inline wrapper — force it to shrink within the row */
.file-info-description-row :deep([data-v-popper-theme]),
.file-info-description-row > :not(.file-info-label) {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.file-info-description {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-info-divider {
  width: 1px;
  height: 16px;
  background: #e5e7eb;
  margin: 0 16px;
  flex-shrink: 0;
}

/* PDF Preview Split View Styles */
/* A definite height, not a minimum: both panes are `height: 100%` columns that
   scroll inside themselves (the left content, the PDF viewer's body). Against
   a min-height the split grows with the left pane's content, the right pane's
   percentage height resolves to nothing, and the PDF viewer's body swells to
   the whole document — its page tracker then reads a quarter of the way down
   that and reports a page in the middle while the visible first page stays a
   blank spacer. */
.main-split-view {
  height: calc(100vh - 256px);
  margin-top: 24px;
}

.left-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-right: 24px;
  overflow: hidden;
}

.left-panel-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 8px;
  min-height: 0;
  /* Custom scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.left-panel-content::-webkit-scrollbar {
  width: 8px;
}

.left-panel-content::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.left-panel-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.left-panel-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.left-panel-content::-webkit-scrollbar {
  width: 8px;
}

.left-panel-content::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.left-panel-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.left-panel-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.split-view-card {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
}

.right-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-left: 24px;
  overflow: hidden;
}

.pdf-panel-header {
  flex-shrink: 0;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
}

.pdf-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.pdf-container-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
  min-height: 0;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

/* Ensure PDF preview takes full height */
.pdf-container-wrapper :deep(.vault-pdf-preview) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.static-models-content {
  padding: 24px;
  background: white;
  border-radius: 0 0 12px 12px;
  border: 1px solid #e5e7eb;
  border-top: none;
}

.static-models-tabs {
  margin-top: 0;
}

.static-models-tabs :deep(.n-tabs-nav) {
  padding: 0 0 12px 0;
  border-bottom: 2px solid #e5e7eb;
}

.static-models-tabs :deep(.n-tabs-tab) {
  font-weight: 500;
  font-size: 14px;
  padding: 8px 16px;
  transition: all 0.3s ease;
}

.static-models-tabs :deep(.n-tabs-tab:hover) {
  color: #2563eb;
}

.static-models-tabs :deep(.n-tabs-tab--active) {
  color: #2563eb;
  font-weight: 600;
}

.static-models-tabs :deep(.n-tabs-bar) {
  background-color: #2563eb;
  height: 3px;
}

/* Tab Header Actions */
.tab-header-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  padding-top: 12px;
}

/* Entity Grid - Modern Card Layout */
.entity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
  margin-top: 8px;
  padding: 0 12px;
}

.entity-card-new {
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  cursor: pointer;
}

.entity-card-new:hover {
  transform: translateY(-4px) scale(1.01);
  box-shadow:
    0 12px 24px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(37, 99, 235, 0.1);
  border-color: #3b82f6;
}

.entity-card-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #e2e8f0;
}

.entity-icon-wrapper {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
}

.entity-card-new:hover .entity-icon-wrapper {
  transform: rotate(3deg) scale(1.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

.entity-icon-wrapper.natural-person {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.entity-icon-wrapper.legal-entity {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
}

.entity-icon-wrapper.location {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.entity-icon-wrapper.obligation {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.entity-icon-wrapper.clause {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.entity-card-title {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

.entity-card-body {
  padding: 12px 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.entity-info-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 10px;
  background: #f8fafc;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 12px;
}

.entity-info-item:hover {
  background: #f1f5f9;
}

.entity-info-item.description {
  flex-direction: column;
  align-items: flex-start;
  padding: 8px 10px;
  line-height: 1.4;
}

/* Responsive adjustments for entity grid */
@media (max-width: 1400px) {
  .entity-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }
}

@media (max-width: 768px) {
  .entity-grid {
    grid-template-columns: 1fr;
  }

  .entity-card-header {
    padding: 12px 14px;
  }

  .entity-card-body {
    padding: 10px 14px 12px;
  }
}

/* Animation for entity cards */
.entity-card-new {
  animation: entityFadeIn 0.4s ease-out;
}

@keyframes entityFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
